import { Component, Input, OnInit } from '@angular/core';
import {
  PayDateInfoEx,
  PayrollStartData,
  PayTypes,
  TimeclockModel
} from '../../../../models/payroll';
import { WarningLevel } from '../../../../models/error-detail';
import { AclService } from '../../../../services/acl.service';
import { AuthService } from '../../../../services/auth.service';
import { CompaniesService } from '../../../../services/companies.service';
import { PayrollService } from '../../../../services/payroll.service';
import { ActivatedRoute, Router } from '@angular/router';
import { SharedService } from '../../../../services/shared.service';
import { forkJoin, of } from 'rxjs';
import { ApiResponse } from '../../../../models/api-response';
import { concatMap, map } from 'rxjs/operators';

@Component({
  selector: 'lib-timeclock-check-legacy',
  templateUrl: './timeclock-check-legacy.component.html',
  styleUrls: ['./timeclock-check-legacy.component.scss'],
  standalone: false
})
export class TimeclockCheckLegacyComponent implements OnInit {
  public clientCode: string;
  public hasValidTimeclock: boolean;
  public isCsr: boolean;
  public loaded: boolean;
  public pdis: PayDateInfoEx[];
  public selCalendarId: number | null;
  public selTimeclockId: number | null;
  public startData: PayrollStartData;
  public timeclockFiles: TimeclockModel[];
  public useTimeClockFile: boolean = false;
  get warningLevels() {
    return WarningLevel;
  }

  constructor(
    private aclService: AclService,
    private authService: AuthService,
    private companyService: CompaniesService,
    private payrollService: PayrollService,
    private route: ActivatedRoute,
    private router: Router,
    private sharedService: SharedService
  ) {}

  ngOnInit() {
    const paramMap = this.route.snapshot.paramMap;
    const queryParams = this.route.snapshot.queryParamMap;
    this.clientCode = paramMap.get('client_code');
    this.selCalendarId = queryParams.has('calendar_id') ? +queryParams.get('calendar_id') : null;

    this.aclService.CurrentUserHasMinimumRole('CSR').subscribe(result => {
      this.isCsr = result;
    });

    // pull company setup and confirm hastimeclock = true
    // if not, redirect to payroll start
    this.companyService
      .getClientSetup(this.clientCode)
      .pipe(
        concatMap(setup => {
          if (!setup.HasTimeclock) {
            return of(false);
          } else {
            // client does have timeclock service
            // fetch payroll start data and earliest due dates
            return this.payrollService.getPayrollStartData(this.clientCode).pipe(
              map(payrollStartData => {
                this.startData = payrollStartData;
                this.pdis = this._getPayDateInfos();
                return true;
              })
            );
          }
        })
      )
      .subscribe((resp: boolean) => {
        if (!resp) {
          // user does not have timeclock service, go to payroll start
          this.sharedService.timeclockSelection = {};
          this._goToPayrollStart();
        } else if (
          this.startData.ActivePayroll &&
          this.startData.ActivePayroll.PayType != PayTypes.Manual
        ) {
          // user is resuming an active payroll.
          const p = this.startData.ActivePayroll;
          // double check that this is the same user that last edited the payroll
          this.sharedService.timeclockSelection = {};
          if (this.authService.isCurrentUser(p.LastEditBy)) {
            // navigate directly to the payroll start page
            this.router.navigate([`/client/${this.clientCode}/payroll`]);
          } else {
            // somehow a different user got here. send them to payroll start
            this._goToPayrollStart();
          }
        } else {
          // there is no active payroll and this client is setup for timeclock
          this._getTimeclockFiles(this.pdis);
        }
      });
  }
  ngOnDestroy(): void {
    // if user selects timeclock id, set the shared service public property
    if (this.selTimeclockId && this.useTimeClockFile) {
      this.sharedService.timeclockSelection.TimeClockFile = this.timeclockFiles.find(
        tc => tc.Id == this.selTimeclockId
      );
    }
  }
  public runPayroll(useFile: boolean = false) {
    this.useTimeClockFile = useFile;
    this.sharedService.timeclockSelection = {};
    this._goToPayrollStart();
  }
  private _goToPayrollStart(params?: {}) {
    // if calendar_id query param exists, append it back to the query params
    // to the payroll start page
    if (this.selCalendarId) {
      params = { ...params, ...{ calendar_id: this.selCalendarId } };
    }

    this.router.navigate([`/client/${this.clientCode}/payroll`], { queryParams: params });
  }

  private _getPayDateInfos(): PayDateInfoEx[] {
    // FIXME: single client location assumption
    const location = this.startData.Locations[0];

    let payDates: PayDateInfoEx[] = [];

    if (this.selCalendarId) {
      // if calendar_id query param exists,
      // only search for timeclock files matching that period end
      payDates.push(location.NextPayDates.find(x => x.CalendarId == this.selCalendarId));
    } else {
      payDates = location.NextPayDates;
    }

    return payDates;
  }

  private _getTimeclockFiles(pdis: PayDateInfoEx[]) {
    // query agile api for timeclock files for each period end date
    // do request in parallel

    const reqs = [];
    for (let pdi of pdis) {
      reqs.push(this.payrollService.findTimeclockFile(this.clientCode, pdi.PeriodEnd));
    }

    forkJoin(reqs).subscribe((files: ApiResponse<TimeclockModel>[]) => {
      this.timeclockFiles = [];
      for (let file of files) {
        // if the file model and messages are both null - there is no file.
        if (file == null || (file.Messages == null && file.Model == null)) {
          continue;
        }

        // timeclock file is valid if there are no messages,
        // or messages does not contain any critical errors
        const valid =
          file.Messages == null ||
          !file.Messages.find(x => x.WarningLevel == WarningLevel.Critical);

        this.timeclockFiles.push(
          new TimeclockModel(file.Model?.Id, file.Model?.PeriodEndDate, valid, file.Messages)
        );
      }

      if (
        this.timeclockFiles.length == 1 &&
        this.timeclockFiles[0].IsValid &&
        this.timeclockFiles[0].Id
      ) {
        this.selTimeclockId = this.timeclockFiles[0].Id;
      } else {
        this.selTimeclockId = null;
      }

      // see if there are and valid timeclock files with no critical errors
      this.hasValidTimeclock =
        this.timeclockFiles.find(
          x => !x.Messages?.find(m => m.WarningLevel == WarningLevel.Critical)
        ) != null;

      this.loaded = true;
    });
  }

  private _getQueryParams(params?: {}) {
    // if calendar_id query param exists, append it back to the query params
    // to the payroll start page
    if (this.route.snapshot.queryParamMap.has('calendar_id')) {
      params = {
        ...params,
        ...{ calendar_id: this.route.snapshot.queryParamMap.get('calendar_id') }
      };
    }

    if (this.route.snapshot.queryParamMap.has('payType')) {
      params = { ...params, ...{ payType: this.route.snapshot.queryParamMap.get('payType') } };
    }

    return params;
  }
}
